/**
 * `<sheet-handle>`: the grab strip at the top of a bottom sheet (a `<dialog>` or `[popover]` shown
 * as a sheet below 64rem; DSComponents §06 Sheet, MobileQuickAdd.dc.html). Pulling it down moves
 * the sheet with the finger; past a quarter of the sheet's height, or with a quick flick, the sheet
 * closes through its normal exit transition, otherwise it springs back.
 *
 * Dragging is only an extra way to close: the close button, Escape and a tap on the scrim keep
 * working, which is the single-pointer alternative WCAG 2.5.7 asks for. The strip is hidden from
 * assistive technology (aria-hidden in the markup).
 */

const CLOSE_SHARE = 0.25;
// A flick: at least this fast downwards (px per ms), averaged over the last FLICK_WINDOW ms of the
// drag (a single pointer step is too noisy; engines space their events differently), and at least
// FLICK_MIN_DISTANCE px long.
const FLICK_SPEED = 0.8;
const FLICK_WINDOW = 80;
const FLICK_MIN_DISTANCE = 24;

class SheetHandle extends HTMLElement {
  #sheet = null;
  #drag = null;
  #abort = null;

  connectedCallback() {
    this.#sheet = this.closest("dialog, [popover]");
    if (!this.#sheet) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener("pointerdown", this.#onDown, { signal });
    this.addEventListener("pointermove", this.#onMove, { signal });
    this.addEventListener("pointerup", this.#onUp, { signal });
    this.addEventListener("pointercancel", this.#onCancel, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    // A sheet can outlive its handle (the filter sheet's content is replaced while the sheet stays
    // open): a drag that was under way hands the sheet back to the stylesheet.
    if (this.#drag) {
      this.#drag = null;
      this.#release();
    }
  }

  #onDown = (event) => {
    if (event.button !== 0) return;
    this.setPointerCapture(event.pointerId);
    this.#drag = { startY: event.clientY, offset: 0, samples: [{ y: event.clientY, t: event.timeStamp }] };
    // Follow the finger directly; the sheet's own transition returns on release.
    this.#sheet.style.transition = "none";
  };

  #onMove = (event) => {
    const drag = this.#drag;
    if (!drag) return;
    drag.samples.push({ y: event.clientY, t: event.timeStamp });
    while (drag.samples.length > 2 && event.timeStamp - drag.samples[0].t > FLICK_WINDOW) drag.samples.shift();
    drag.offset = Math.max(0, event.clientY - drag.startY);
    this.#sheet.style.translate = `0 ${drag.offset}px`;
  };

  #onUp = () => {
    const drag = this.#drag;
    if (!drag) return;
    this.#drag = null;
    const first = drag.samples[0];
    const last = drag.samples.at(-1);
    const speed = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
    const flick = speed > FLICK_SPEED && drag.offset > FLICK_MIN_DISTANCE;
    const close = drag.offset > this.#sheet.offsetHeight * CLOSE_SHARE || flick;
    // Hand the position back to the stylesheet in the same frame: the transition then runs from
    // where the finger let go, down to the closed state or back up to the open one.
    this.#release();
    if (!close) return;
    if (this.#sheet instanceof HTMLDialogElement) this.#sheet.close();
    else this.#sheet.hidePopover();
  };

  #onCancel = () => {
    this.#drag = null;
    this.#release();
  };

  #release() {
    this.#sheet.style.removeProperty("transition");
    this.#sheet.style.removeProperty("translate");
  }
}

customElements.define("sheet-handle", SheetHandle);
